import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { mapAuthError } from '@mybusinessmate/api-client';
import { OTP_LENGTH } from '@mybusinessmate/schema';
import { AuthHeading } from '../../../../components/auth/auth-heading';
import { Banner } from '../../../../components/ui/banner';
import { Button } from '../../../../components/ui/button';
import { OtpInput } from '../../../../components/ui/otp-input';
import { useToast } from '../../../../components/ui/toast';
import { textLinkClass } from '../../../../components/ui/text-link';
import { authClient } from '../../../../lib/api';
import { useOnboardingStore } from '../../../../lib/onboarding-store';
import { formatCountdown, useCountdown } from '../../../../lib/use-countdown';
import { refreshSession } from '../../../../lib/session';

export const Route = createFileRoute('/subdomain/app/_auth/verify-email')({
  // Nothing to verify without an email from the sign-up step (e.g. a direct visit or a closed tab).
  beforeLoad: () => {
    if (!useOnboardingStore.getState().pendingEmail) throw redirect({ to: '/subdomain/app/sign-up' });
  },
  component: VerifyEmailPage,
});

const RESEND_SECONDS = 30;

function VerifyEmailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const email = useOnboardingStore((s) => s.pendingEmail) ?? '';
  const { remaining, done, restart } = useCountdown(RESEND_SECONDS);

  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const verify = async (code: string) => {
    setSubmitting(true);
    setError(undefined);
    const { error: authError } = await authClient.emailOtp.verifyEmail({ email, otp: code });
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(
        mapped.kind === 'invalid_code'
          ? 'That code didn’t work. Check it and try again.'
          : mapped.kind === 'too_many_attempts'
            ? 'Too many wrong codes. Request a new one.'
            : mapped.message,
      );
      setSubmitting(false);
      return;
    }
    // Verifying signs the user in (autoSignInAfterVerification), so the session now exists.
    await refreshSession(queryClient);
    await navigate({ to: '/subdomain/app/audit' });
  };

  const resend = async () => {
    setError(undefined);
    setOtp('');
    const { error: authError } = await authClient.emailOtp.sendVerificationOtp({ email, type: 'email-verification' });
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(
        mapped.kind === 'rate_limited' ? 'Please wait a moment before asking for another code.' : mapped.message,
      );
      return;
    }
    restart();
    toast({ title: 'New code sent', description: `Check ${email}` });
  };

  return (
    <>
      <AuthHeading title="Check your inbox" subtitle={`Enter the 6-digit code we sent to ${email}`} />

      <div className="flex flex-col gap-(--mbm-space-3)">
        <OtpInput
          value={otp}
          onChange={(value) => {
            setOtp(value);
            setError(undefined);
          }}
          onComplete={(code) => void verify(code)}
          disabled={submitting}
          error={Boolean(error)}
          autoFocus
        />
        {error ? (
          <p role="alert" className="text-body-small text-feedback-error">
            {error}
          </p>
        ) : null}
      </div>

      <p className="flex justify-center gap-(--mbm-space-1) text-body-small text-text-secondary">
        Didn’t get it?{' '}
        {done ? (
          <button type="button" onClick={() => void resend()} className={textLinkClass}>
            Resend code
          </button>
        ) : (
          <span className="text-label-medium text-brand-primary">Resend code in {formatCountdown(remaining)}</span>
        )}
      </p>
      <p className="text-body-xsmall text-text-tertiary">Tip: check your spam or promotions folder.</p>

      <Button fullWidth disabled={otp.length !== OTP_LENGTH} loading={submitting} onClick={() => void verify(otp)}>
        Verify
      </Button>
      {!email ? <Banner tone="warning">We lost track of your email. Go back and sign up again.</Banner> : null}
    </>
  );
}
